import { HttpErrorResponse } from '@angular/common/http';

// QuickBooks API Models
//
// QuickBooks connections arrive as ordinary integration rows from
// /integration/get-by-application, with everything provider-specific living in
// the `metadata` bag. The bag is Dictionary<string,string> on the wire, so every
// value is a string regardless of its real type — parseQuickBooksMetadata below
// is the single place that turns it back into something typed. Nothing
// downstream should read the raw bag.

/**
 * Connection health as reported by the backend. 'Unknown' is not a wire value —
 * it is what an unrecognised status parses to, so that a value we don't know
 * about fails toward showing Reconnect rather than silently hiding it.
 */
export enum QuickBooksConnectionStatus {
  Connected = 'Connected',
  RequiresReauthorization = 'RequiresReauthorization',
  Revoked = 'Revoked',
  Unknown = 'Unknown',
}

/** The metadata bag, parsed. */
export interface QuickBooksConnectionDetails {
  connectionId: number;
  realmId: string;
  /** Null until the first company-info call succeeds. */
  companyName: string | null;
  environment: string;
  status: QuickBooksConnectionStatus;
  connectedAt: Date | null;
  /**
   * Which database the synced data lands in.
   *
   * Null means there is no destination: the connection is authorized but not
   * synchronising, and /sync will refuse it. That is a second axis independent
   * of `status` — a connection can be perfectly Connected and still going
   * nowhere — so the UI has to show both.
   */
  targetDatabaseIntegrationId: number | null;
  connectedByEmail: string | null;
  /** Null when no API call has ever succeeded. */
  lastSuccessfulApiCall: Date | null;
}

/** True for any status that should surface a Reconnect action. */
export function needsReconnect(status: QuickBooksConnectionStatus): boolean {
  return status !== QuickBooksConnectionStatus.Connected;
}

/**
 * True when the connection has somewhere to put data. Authorization succeeding
 * is not enough — without a target database nothing syncs.
 */
export function isSynchronising(details: QuickBooksConnectionDetails | null): boolean {
  return !!details && details.targetDatabaseIntegrationId !== null;
}

/** Human wording for a status. Shared so the table and the dialog agree. */
export function describeStatus(status: QuickBooksConnectionStatus): string {
  switch (status) {
    case QuickBooksConnectionStatus.Connected:
      return 'Connected';
    case QuickBooksConnectionStatus.RequiresReauthorization:
      return 'Needs reauthorization';
    case QuickBooksConnectionStatus.Revoked:
      return 'Revoked';
    default:
      return 'Unknown';
  }
}

/**
 * Turns an integration row's metadata bag into a typed connection.
 *
 * Returns null when the bag is missing the handle we need — a row we cannot act
 * on should degrade in the UI rather than break the whole list.
 */
export function parseQuickBooksMetadata(
  metadata: Record<string, string> | null | undefined
): QuickBooksConnectionDetails | null {
  if (!metadata) {
    return null;
  }

  const connectionId = Number(metadata['connectionId']);
  if (!Number.isFinite(connectionId)) {
    console.warn('QuickBooks row has no usable connectionId', metadata);
    return null;
  }

  // The bag sends empty strings rather than omitting keys, so "" is the
  // meaningful "no value" here, not undefined.
  const targetId = Number(metadata['targetDatabaseIntegrationId']);

  return {
    connectionId,
    realmId: metadata['realmId'] ?? '',
    companyName: metadata['companyName'] || null,
    environment: metadata['environment'] ?? '',
    status: parseStatus(metadata['status']),
    connectedAt: parseTimestamp(metadata['connectedAt']),
    targetDatabaseIntegrationId: metadata['targetDatabaseIntegrationId'] && Number.isFinite(targetId)
      ? targetId
      : null,
    connectedByEmail: metadata['connectedByEmail'] || null,
    lastSuccessfulApiCall: parseTimestamp(metadata['lastSuccessfulApiCall']),
  };
}

function parseTimestamp(raw: string | undefined): Date | null {
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function parseStatus(raw: string | undefined): QuickBooksConnectionStatus {
  switch (raw) {
    case QuickBooksConnectionStatus.Connected:
      return QuickBooksConnectionStatus.Connected;
    case QuickBooksConnectionStatus.RequiresReauthorization:
      return QuickBooksConnectionStatus.RequiresReauthorization;
    case QuickBooksConnectionStatus.Revoked:
      return QuickBooksConnectionStatus.Revoked;
    default:
      console.warn('Unrecognised QuickBooks connection status:', raw);
      return QuickBooksConnectionStatus.Unknown;
  }
}

// ============================================
// In-flight authorization
// ============================================

export const QUICKBOOKS_SESSION_KEYS = {
  /**
   * Written when the user starts an authorization, read when Intuit redirects
   * back. Session storage rather than local storage on purpose: it is scoped to
   * the tab that began the flow, so switching applications in another tab can't
   * change which application the callback completes against.
   */
  PENDING_CONNECT: 'bhub.quickbooks.pendingConnect',
} as const;

export interface QuickBooksPendingConnect {
  /** The application that was active when Connect was clicked. */
  applicationId: number;
  /** Where to send the user once the connection completes. */
  returnUrl: string;
}

// ============================================
// Endpoint payloads
// ============================================

export interface QuickBooksConnectRequest {
  /** Optional. Omitted or null authorizes the connection without a destination. */
  targetDatabaseIntegrationId?: number | null;
}

export interface QuickBooksConnectResponse {
  authorizationUrl: string;
}

export interface QuickBooksSetTargetDatabaseRequest {
  connectionId: number;
  /** Null clears the destination, stopping sync until one is chosen again. */
  targetDatabaseIntegrationId: number | null;
}

export interface QuickBooksCallbackRequest {
  code: string;
  realmId: string;
  state: string;
}

/** Returned by /quickbooks/callback. Mirrors the connection table directly. */
export interface QuickBooksConnection {
  connectionId: number;
  realmId: string;
  companyName: string | null;
  environment: string;
  status: string;
  connectedAt: string;
}

/** One syncable entity, from the mapping registry the service holds. */
export interface QuickBooksEntity {
  entity: string;
  datasetName: string;
  fieldCount: number;
  datasetId: number | null;
  /**
   * Whether a dataset exists in the connection's *current* destination. Not a
   * sync timestamp — no per-entity history is persisted — so changing the
   * target correctly flips this back to false.
   */
  hasDataset: boolean;
}

export interface QuickBooksEntitiesResponse {
  connectionId: number;
  targetDatabaseIntegrationId: number | null;
  /**
   * Whole-connection, not per-entity: when there's no destination nothing can
   * sync and the reason is the same every time.
   */
  canSync: boolean;
  blockedReason: string | null;
  entities: QuickBooksEntity[];
}

export interface QuickBooksSyncResponse {
  entity: string;
  datasetId: number;
  datasetName: string;
  /** False means an existing dataset was refreshed rather than created. */
  datasetCreated: boolean;
  recordsRead: number;
  /**
   * Values that came back from Intuit but didn't make it into the table. An
   * empty array is the normal case; anything in here silently degrades every
   * metric built on the dataset, so it must be surfaced rather than counted.
   *
   * Typed loosely because the element shape isn't pinned down yet.
   */
  unconvertedValues: unknown[];
}

/**
 * Maps the documented status codes onto words a user can act on. Shared so the
 * table, the dialogs, and the callback all say the same thing.
 */
export function describeQuickBooksError(error: HttpErrorResponse): string {
  switch (error.status) {
    case 400:
      // State refused, invalid target, unknown entity, or sync with no
      // destination — the server deliberately doesn't distinguish them.
      return 'This request could not be completed. Please check the connection and try again.';
    case 403:
      return 'You need to be an owner or admin to manage QuickBooks.';
    case 409:
      return 'This QuickBooks connection has expired. Reconnect to continue.';
    case 502:
      return 'QuickBooks could not be reached. Please try again.';
    case 503:
      return 'QuickBooks is not configured for this environment.';
    default:
      return 'Something went wrong talking to QuickBooks.';
  }
}

/** Renders an unconverted value without assuming it's a string. */
export function formatUnconvertedValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export interface QuickBooksCompanyInfo {
  companyId: string;
  companyName: string;
  legalName: string;
  country: string;
}

export interface QuickBooksDisconnectResponse {
  removed: boolean;
  /**
   * False means the connection is gone from BusinessHub but Intuit refused the
   * revocation — the customer may still see BusinessHub listed inside
   * QuickBooks and needs to disconnect there too. Must be surfaced persistently,
   * not in a transient toast.
   */
  revokedUpstream: boolean;
}
